import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { type ResultsProps } from "@usereactify/search";

import { useCore } from "~/hooks/useCore";
import { useWindowSize } from "~/hooks/useWindowSize";

import { ComponentProps } from "~/types/components";

import { SearchListEmpty } from "../List/SearchListEmpty";
import { SearchListError } from "../List/SearchListError";
import NumberedPagination from "../Pagination/NumberedPagination";
import { SearchResultsCalloutCard } from "./SearchResultsCalloutCard";
import SearchResultsProductCard from "./SearchResultsProductCard";

export type SearchResultsInputProps = ComponentProps & {
  collection?: {
    handle?: string;
    title?: string;
  };
};

export type SearchResultsOutputProps = SearchResultsInputProps & {
  renderError: (props: any) => JSX.Element;
  renderResultCard: (props: any) => JSX.Element;
  renderResultCardCallout: (props: any) => JSX.Element;
  renderPaginationNumbered: (
    props: React.ComponentProps<
      NonNullable<ResultsProps["renderPaginationNumbered"]>
    >,
  ) => JSX.Element;
  renderNoResults: () => JSX.Element | null;
  numColumns: number;
};

export const withSearchResults =
  (Component: FC<SearchResultsOutputProps>) =>
  ({ name = "SearchResults", collection }: SearchResultsInputProps) => {
    const {
      windowSize: { isMD },
    } = useWindowSize();
    const cardRenderCount = useRef(0);
    const {
      helpers: { storage },
    } = useCore();

    const renderError = (props) => {
      return <SearchListError {...props} />;
    };

    const renderNoResults = () => {
      return collection ? <SearchListEmpty /> : null;
    };

    const renderResultCard = useCallback(
      (props) => {
        cardRenderCount.current += 1;
        return <SearchResultsProductCard {...props} collection={collection} />;
      },
      [cardRenderCount],
    );

    const renderResultCardCallout = useCallback(
      (props) => {
        cardRenderCount.current += 1;
        return <SearchResultsCalloutCard {...props} />;
      },
      [cardRenderCount],
    );

    const renderPaginationNumbered: SearchResultsOutputProps["renderPaginationNumbered"] =
      useCallback(
        (props) => {
          return <NumberedPagination {...props} />;
        },
        [cardRenderCount],
      );

    const calculateColumns = useCallback(() => {
      if (isMD) {
        return 4;
      }
      return 2;
    }, [isMD]);

    const numColumns = calculateColumns();

    useEffect(() => {
      storage.set("headless:collection_last", collection);
    }, [collection]);

    Component.displayName = name;

    return useMemo(
      () => (
        <Component
          renderError={renderError}
          renderPaginationNumbered={renderPaginationNumbered}
          renderNoResults={renderNoResults}
          renderResultCard={renderResultCard}
          renderResultCardCallout={renderResultCardCallout}
          numColumns={numColumns}
          collection={collection}
        />
      ),
      [isMD],
    );
  };
